import { forwardRef, type ReactNode, type ReactElement } from 'react'
import { isTrue } from '@terros/common'
import { useMergedRef } from '@mantine/hooks'
import { createPolymorphicComponent, Group, Skeleton, Stack, Text, type TextProps } from '@mantine/core'
import { getInitialism } from '@front/model'
import { AvatarBlurhash, AvatarSkeleton } from '../AvatarBlurhash'
import { type AvatarProps, type EntityAvatarProps } from './types'

type Props = AvatarProps &
  EntityAvatarProps & {
    textProps?: TextProps
    details?: ReactNode
    avatar?: ReactNode
  }

const _PrefetchedEntityInfo = forwardRef<HTMLDivElement | HTMLSpanElement, Props>(
  (
    {
      name,
      initials,
      size = 'sm',
      avatarOnly,
      companyName,
      textProps,
      details,
      isDeleted,
      avatarUrl,
      avatarBlurhash,
      noBlurhash,
      color,
      avatar,
      inline,
      hideAvatar,
    },
    ref
  ) => {
    const setRef = useMergedRef(ref)
    const labelString = companyName ? `${name} (${companyName})` : name
    const isArchived = isTrue(isDeleted)
    const avatarNode = avatar ?? (
      <AvatarBlurhash
        avatarBlurhash={avatarBlurhash}
        avatarUrl={avatarUrl}
        color={color}
        initials={initials ?? getInitialism(name)}
        noBlurhash={noBlurhash}
        size={inline ? INLINE_AVATAR_SIZE : size}
      />
    )
    if (inline) {
      return (
        <InlineEntityInfo
          ref={setRef}
          name={labelString}
          isArchived={isArchived}
          textProps={textProps}
          avatar={hideAvatar ? undefined : avatarNode}
        />
      )
    }
    return (
      <Group ref={setRef} wrap='nowrap' align='center' gap='sm'>
        {avatarNode}
        {!avatarOnly && (
          <Stack gap={0}>
            <Text
              size={size}
              fs={isArchived ? 'italic' : undefined}
              c={isArchived ? 'dimmed' : undefined}
              {...textProps}
            >
              {labelString}
            </Text>
            {details && (
              <Text size='xs' c='dimmed'>
                {details}
              </Text>
            )}
          </Stack>
        )}
      </Group>
    )
  }
)

// Fits within a line of text, so the avatar doesn't push the line taller
const INLINE_AVATAR_SIZE = 18

type InlineEntityInfoProps = {
  name: string
  isArchived: boolean
  avatar?: ReactNode
  textProps?: TextProps
}

/** Avatar and name that sit on the surrounding text's baseline, like an inline icon */
const InlineEntityInfo = forwardRef<HTMLSpanElement, InlineEntityInfoProps>(
  ({ name, isArchived, avatar, textProps }, ref): ReactElement => (
    <span ref={ref} className='whitespace-nowrap'>
      {avatar && <span className='pr-md inline-block align-[-4px]'>{avatar}</span>}
      <Text span inherit fs={isArchived ? 'italic' : undefined} c={isArchived ? 'dimmed' : undefined} {...textProps}>
        {name}
      </Text>
    </span>
  )
)

InlineEntityInfo.displayName = 'InlineEntityInfo'

_PrefetchedEntityInfo.displayName = 'PrefetchedEntityInfo'

export const PrefetchedEntityInfo = createPolymorphicComponent<'div', Props>(_PrefetchedEntityInfo)

export const LoadingEntityInfo = ({ size, avatarOnly }: AvatarProps): ReactElement => {
  return (
    <Group wrap='nowrap' align='center' gap='sm'>
      <AvatarSkeleton size={size} />
      {!avatarOnly && <Skeleton h={20} w={100} />}
    </Group>
  )
}
